import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Calendar, MapPin, ClipboardCheck, Globe, Users, Clock,
  ArrowLeft, Route, FileText, Languages, CheckCircle, AlertCircle
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OperationsDashboard() {
  const [activeTab, setActiveTab] = useState("scheduling");
  const [loading, setLoading] = useState(false);
  
  // Scheduling state
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [skillRequirements, setSkillRequirements] = useState(null);
  
  // Compliance state
  const [complianceReport, setComplianceReport] = useState(null);
  const [cmsMeasures, setCmsMeasures] = useState({});
  
  // Translation state
  const [translationText, setTranslationText] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("es");
  const [translationResult, setTranslationResult] = useState(null);
  const [languages, setLanguages] = useState({});
  const [commonPhrases, setCommonPhrases] = useState({});

  useEffect(() => {
    loadSkillRequirements();
    loadCmsMeasures();
    loadLanguages();
    loadCommonPhrases();
  }, []);

  const loadSkillRequirements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scheduling-optimizer/skill-requirements`);
      const data = await res.json();
      setSkillRequirements(data);
    } catch (err) {
      console.error("Failed to load skill requirements:", err);
    }
  };

  const loadCmsMeasures = async () => {
    try {
      const res = await fetch(`${API_URL}/api/compliance-reports/cms-measures`);
      const data = await res.json();
      setCmsMeasures(data);
    } catch (err) {
      console.error("Failed to load CMS measures:", err);
    }
  };

  const loadLanguages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/translation/languages`);
      const data = await res.json();
      setLanguages(data.languages || {});
    } catch (err) {
      console.error("Failed to load languages:", err);
    }
  };

  const loadCommonPhrases = async () => {
    try {
      const res = await fetch(`${API_URL}/api/translation/phrases/all`);
      const data = await res.json();
      setCommonPhrases(data.phrases || {});
    } catch (err) {
      console.error("Failed to load common phrases:", err);
    }
  };

  const runOptimization = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduling-optimizer/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          staff: [
            {
              staff_id: "staff_001",
              name: "Sarah Johnson, RN",
              role: "RN",
              skills: ["wound_care", "iv_therapy", "symptom_management"],
              home_location: { address: "123 Main St", zip_code: "90210" },
              available_start: "08:00",
              available_end: "17:00",
              max_visits_per_day: 8
            },
            {
              staff_id: "staff_002",
              name: "Mike Chen, SW",
              role: "SW",
              skills: ["psychosocial_support", "family_education", "bereavement"],
              home_location: { address: "456 Oak Ave", zip_code: "90211" },
              available_start: "09:00",
              available_end: "18:00",
              max_visits_per_day: 6
            }
          ],
          visits: [
            {
              patient_id: "pt_001",
              patient_name: "John Smith",
              location: { address: "789 Elm St", zip_code: "90210" },
              required_skills: ["wound_care"],
              visit_type: "routine",
              priority: "routine"
            },
            {
              patient_id: "pt_002",
              patient_name: "Mary Johnson",
              location: { address: "321 Pine Rd", zip_code: "90211" },
              required_skills: ["symptom_management"],
              visit_type: "prn",
              priority: "urgent"
            },
            {
              patient_id: "pt_003",
              patient_name: "Robert Brown",
              location: { address: "555 Maple Dr", zip_code: "90210" },
              required_skills: ["psychosocial_support"],
              visit_type: "routine",
              priority: "routine"
            }
          ],
          optimization_goal: "minimize_travel"
        })
      });
      const data = await res.json();
      setOptimizationResult(data);
      toast.success("Schedule optimized successfully");
    } catch (err) {
      toast.error("Failed to optimize schedule");
    }
    setLoading(false);
  };

  const generateComplianceReport = async (reportType) => {
    setLoading(true);
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const res = await fetch(`${API_URL}/api/compliance-reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          report_type: reportType,
          start_date: thirtyDaysAgo.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
          format: "detailed"
        })
      });
      const data = await res.json();
      setComplianceReport(data);
      toast.success(`${reportType.replace('_', ' ')} report generated`);
    } catch (err) {
      toast.error("Failed to generate report");
    }
    setLoading(false);
  };

  const translateText = async () => {
    if (!translationText.trim()) {
      toast.error("Please enter text to translate");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/translation/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: translationText,
          source_language: "en",
          target_language: targetLanguage,
          context: "medical"
        })
      });
      const data = await res.json();
      setTranslationResult(data);
      toast.success("Translation complete");
    } catch (err) {
      toast.error("Translation failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/hospice-ehr" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Operations Suite</h1>
              <p className="text-slate-600">Scheduling, compliance, and translation tools</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Route className="w-3 h-3 mr-1" />
              AI Optimized
            </Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Visits Today</p>
                  <p className="text-2xl font-bold text-blue-900">24</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Compliance Score</p>
                  <p className="text-2xl font-bold text-green-900">94%</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Travel Time Saved</p>
                  <p className="text-2xl font-bold text-purple-900">2.5h</p>
                </div>
                <Clock className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-teal-600">Languages</p>
                  <p className="text-2xl font-bold text-teal-900">{Object.keys(languages).length}</p>
                </div>
                <Globe className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="scheduling" className="flex items-center gap-2" data-testid="scheduling-tab">
              <Route className="w-4 h-4" />
              Scheduling Optimizer
            </TabsTrigger>
            <TabsTrigger value="compliance" className="flex items-center gap-2" data-testid="compliance-tab">
              <ClipboardCheck className="w-4 h-4" />
              Compliance Reports
            </TabsTrigger>
            <TabsTrigger value="translation" className="flex items-center gap-2" data-testid="translation-tab">
              <Languages className="w-4 h-4" />
              Translation
            </TabsTrigger>
          </TabsList>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Route className="w-5 h-5 text-blue-600" />
                    Route Optimization
                  </CardTitle>
                  <CardDescription>
                    AI-powered visit scheduling and route optimization
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Run optimization to assign visits to staff based on skills, location, and availability.
                  </p>
                  
                  <Button 
                    onClick={runOptimization} 
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="optimize-btn"
                  >
                    {loading ? "Optimizing..." : "Run Optimization"}
                  </Button>

                  {skillRequirements && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="font-medium text-sm mb-2">Available Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.keys(skillRequirements.skill_categories || {}).map(skill => (
                          <Badge key={skill} variant="outline" className="text-xs">
                            {skill.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Optimization Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {optimizationResult ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <span>Efficiency Score</span>
                        <Badge className="bg-green-500">{optimizationResult.efficiency_score}%</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <span>Total Travel Time</span>
                        <span className="font-medium">{optimizationResult.total_travel_time} mins</span>
                      </div>

                      {optimizationResult.routes?.map((route, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-medium">{route.staff_name}</span>
                            <Badge variant="outline">{route.utilization_percentage}% utilized</Badge>
                          </div>
                          <div className="space-y-2">
                            {route.visits?.map((visit, vIdx) => (
                              <div key={vIdx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                                <div>
                                  <p className="font-medium">{visit.patient_name}</p>
                                  <p className="text-xs text-slate-500">{visit.arrival_time} - {visit.departure_time}</p>
                                </div>
                                <Badge variant={visit.priority === "urgent" ? "destructive" : "outline"}>
                                  {visit.priority}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {optimizationResult.unassigned_visits?.length > 0 && (
                        <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                          <p className="font-medium text-orange-700 mb-2">Unassigned Visits</p>
                          {optimizationResult.unassigned_visits.map((v, idx) => (
                            <div key={idx} className="text-sm">
                              {v.patient_name} - {v.reason}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-12">
                      <Route className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Run optimization to see results</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    Report Generator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    onClick={() => generateComplianceReport("cms_quality")}
                    disabled={loading}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-2" />
                    CMS Quality Report
                  </Button>
                  <Button 
                    onClick={() => generateComplianceReport("qapi")}
                    disabled={loading}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    QAPI Report
                  </Button>
                  <Button 
                    onClick={() => generateComplianceReport("survey_prep")}
                    disabled={loading}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Survey Prep Checklist
                  </Button>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Report Results</CardTitle>
                </CardHeader>
                <CardContent>
                  {complianceReport ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Report Type:</span>
                        <Badge>{complianceReport.report_type}</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Period:</span>
                        <span>{complianceReport.period_start} to {complianceReport.period_end}</span>
                      </div>

                      {complianceReport.summary && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium mb-2">Summary</p>
                          {Object.entries(complianceReport.summary).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="capitalize">{key.replace('_', ' ')}</span>
                              <span className="font-medium">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {complianceReport.recommendations?.length > 0 && (
                        <div>
                          <p className="font-medium mb-2">Recommendations</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {complianceReport.recommendations.map((r, idx) => (
                              <li key={idx}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          Export PDF
                        </Button>
                        <Button variant="outline" size="sm">
                          Export Excel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-12">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Select a report type to generate</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* CMS Measures Reference */}
            <Card>
              <CardHeader>
                <CardTitle>CMS Hospice Quality Measures</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(cmsMeasures).map(([key, measure]) => (
                    <div key={key} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">{key}</Badge>
                        <span className="text-sm text-slate-500">Target: {measure.target}%</span>
                      </div>
                      <p className="font-medium text-sm">{measure.name}</p>
                      <p className="text-xs text-slate-600 mt-1">{measure.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Translation Tab */}
          <TabsContent value="translation" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Languages className="w-5 h-5 text-teal-600" />
                    Translate Text
                  </CardTitle>
                  <CardDescription>
                    AI-powered medical translation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Text to Translate (English)</Label>
                    <Textarea
                      value={translationText}
                      onChange={(e) => setTranslationText(e.target.value)}
                      placeholder="Enter text to translate..."
                      rows={4}
                      data-testid="translation-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Target Language</Label>
                    <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(languages).map(([code, lang]) => (
                          <SelectItem key={code} value={code}>
                            {lang.name} ({lang.native})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={translateText} 
                    disabled={loading}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    data-testid="translate-btn"
                  >
                    {loading ? "Translating..." : "Translate"}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Translation Result</CardTitle>
                </CardHeader>
                <CardContent>
                  {translationResult ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Original (English)</p>
                        <p>{translationResult.original_text}</p>
                      </div>
                      
                      <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                        <p className="text-sm text-teal-600 mb-1">
                          Translated ({languages[translationResult.target_language]?.name})
                        </p>
                        <p className="text-lg">{translationResult.translated_text}</p>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <span>Confidence: {(translationResult.confidence * 100).toFixed(0)}%</span>
                        <Button variant="ghost" size="sm" onClick={() => navigator.clipboard.writeText(translationResult.translated_text)}>
                          Copy
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-12">
                      <Languages className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Enter text to translate</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Common Phrases */}
            <Card>
              <CardHeader>
                <CardTitle>Common Hospice Phrases</CardTitle>
                <CardDescription>Pre-translated phrases for quick use</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(commonPhrases).map(([category, phrase]) => (
                    <div 
                      key={category} 
                      className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50"
                      onClick={() => setTranslationText(phrase)}
                    >
                      <p className="font-medium text-sm capitalize">{category.replace('_', ' ')}</p>
                      <p className="text-sm text-slate-600 mt-1">{phrase}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
